import { ExclamationTriangleIcon, InformationCircleIcon, CheckCircleIcon, XCircleIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { createContext, ReactNode, useContext } from 'react';
import { getChildrenByType } from '../../../utils';
// tailwindcss jit definitions
// text-blue-400 text-blue-800 bg-blue-50 text-blue-500 bg-blue-100 ring-offset-blue-50 ring-blue-600 text-blue-700
// text-green-400 text-green-800 bg-green-50 text-green-500 bg-green-100 ring-offset-green-50 ring-green-600 text-green-700
// text-yellow-400 text-green-800 bg-yellow-50 text-yellow-500 bg-yellow-100 ring-offset-yellow-50 ring-yellow-600 text-yellow-700
// text-red-400 text-green-800 bg-red-50 text-red-500 bg-red-100 ring-offset-red-50 ring-red-600 text-red-700

const colors = {
    info: 'blue',
    success: 'green',
    warn: 'yellow',
    error: 'red',
};

export type StandardAlertProps = {
    type: 'info' | 'success' | 'warn' | 'error';
    title: string;
    children: ReactNode | ReactNode[];
    description?: string | string[];
    onClose?: () => void;
};

const ColorContext = createContext('info');

function StandardAlert({ type, title, children, description, onClose }: StandardAlertProps): JSX.Element {
    const color = colors[type];
    const actions = getChildrenByType(children, Button);

    return (
        <div className={`rounded-md bg-${color}-50 p-4`}>
            <div>
                {/* Top */}
                <div className="flex">
                    {/* Icon */}
                    <div className="flex-shrink-0">
                        {type === 'info' && <InformationCircleIcon className={`h-5 w-5 text-${color}-400`} aria-hidden="true" />}
                        {type === 'success' && <CheckCircleIcon className={`h-5 w-5 text-${color}-400`} aria-hidden="true" />}
                        {type === 'warn' && <ExclamationTriangleIcon className={`h-5 w-5 text-${color}-400`} aria-hidden="true" />}
                        {type === 'error' && <XCircleIcon className={`h-5 w-5 text-${color}-400`} aria-hidden="true" />}
                    </div>
                    {/* Title */}
                    <div className="ml-3">
                        <h3 className={`text-sm font-medium text-${color}-800`}>{title}</h3>
                    </div>
                    {/* Close Button */}
                    {onClose && (
                        <div className="ml-auto pl-3">
                            <div className="-mx-1.5 -my-1.5">
                                <button
                                    onClick={onClose}
                                    type="button"
                                    className={`inline-flex bg-${color}-50 rounded-md p-1.5 text-${color}-500 hover:bg-${color}-100 
                                                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-${color}-50 
                                                focus:ring-${color}-600`}
                                >
                                    <span className="sr-only">Dismiss</span>
                                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                {/* body */}
                {description && (
                    <div className={`ml-7 mt-2 text-sm text-${color}-700`}>
                        {Array.isArray(description) ? (
                            <ul role="list" className="list-disc pl-5 space-y-1">
                                {description.map((d, i) => (
                                    <li key={i}>{d}</li>
                                ))}
                            </ul>
                        ) : (
                            <p className="ml-1">{description}</p>
                        )}
                    </div>
                )}
                {actions.length > 0 && (
                    <ColorContext.Provider value={color}>
                        <div className="mt-4 ml-8">
                            <div className="-mx-2 -my-1.5 flex gap-3">{actions.map((button) => button)}</div>
                        </div>
                    </ColorContext.Provider>
                )}
            </div>
        </div>
    );
}

type ButtonProps = {
    children: string;
    onClick: () => void;
};

function Button({ children, onClick }: ButtonProps): JSX.Element {
    const color = useContext(ColorContext);

    return (
        <button
            onClick={onClick}
            type="button"
            className={`bg-${color}-50 px-2 py-1.5 rounded-md text-sm font-medium text-${color}-800 hover:bg-${color}-100 
                          focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-${color}-50 focus:ring-${color}-600`}
        >
            {children}
        </button>
    );
}

StandardAlert.Button = Button;

export default StandardAlert;
